import * as React from 'react';
import { memo } from 'react';

import TerrificBridge from 'react-terrific-bridge';

import { LayoutTriggers } from 'defs/layout';
import i18n from 'helper/i18n';
import { delayed } from 'lib/Job';
import { TUTORIAL_ID } from 'types/Tutorial';

import { WithLayoutPropsType } from 'components/hoc/withLayout';
import { TriggeredTutorialType } from 'store/tutorial';
import { TutorialIdType } from 'types/Tutorial';

import CtaWave from 'components/atoms/CtaWave';
import withLayout from 'components/hoc/withLayout';
import TutorialCardDetail from 'components/molecules/Tutorial/TutorialCardDetail';
import TutorialFeedCalendar from 'components/molecules/Tutorial/TutorialFeedCalendar';
import TutorialFeedOverview from 'components/molecules/Tutorial/TutorialFeedOverview';
import noop from 'helper/noop';
import { createRef } from 'react';

export type TutorialPropsType = any & WithLayoutPropsType;

export const TUTORIAL_TERRIFIC_ACTION = {
	tutorialOpened: 'tutorialOpened',
	tutorialClosed: 'tutorialClosed',
	scrollToTop: 'scrollToTop',
};
export type TutorialTerrificActionEnumType = keyof typeof TUTORIAL_TERRIFIC_ACTION;

export class Tutorial extends React.Component<TutorialPropsType> {
	public static displayName = 'Tutorial';

	private componentRef: React.RefObject<HTMLDivElement>;

	constructor(props: TutorialPropsType) {
		super(props);
		this.componentRef = createRef();
	}

	public componentDidMount() {
		TerrificBridge.registerComponent(this.componentRef.current);
		this.setVisibleTutorial(this.props);
	}

	public componentWillUnmount() {
		TerrificBridge.unregisterComponent(this.componentRef.current);
	}

	public setVisibleTutorial(props: TutorialPropsType) {
		const { areTutorialsLoaded, visibleTutorial } = props;

		if (!areTutorialsLoaded) {
			return;
		}

		if (typeof visibleTutorial !== 'string' || visibleTutorial === null) {
			return;
		}

		if (visibleTutorial === TUTORIAL_ID.FEED_OVERVIEW || visibleTutorial === TUTORIAL_ID.FEED_CALENDAR) {
			this.props.doGoTop(LayoutTriggers.Tutorial);
			this.props.doLockBody(LayoutTriggers.Tutorial, {
				top: true,
			});
		}

		return;
	}

	public componentDidUpdate(prevProps: TutorialPropsType) {
		this.setVisibleTutorial(this.props);
	}

	public handleFireTerrificAction = (action: TutorialTerrificActionEnumType, callback: any = noop) => {
		TerrificBridge.action(this.componentRef.current, action, callback);
	};

	public handleClick = (event: any) => {
		event.preventDefault();

		const { visibleTutorial } = this.props;

		if (!this.visibleTutorial || this.visibleTutorial === null) {
			return;
		}

		this.props.doUpdateTutorialFlag(this.visibleTutorial, true);
		this.handleFireTerrificAction(TUTORIAL_TERRIFIC_ACTION.tutorialClosed as any);

		if (visibleTutorial === TUTORIAL_ID.FEED_OVERVIEW || visibleTutorial === TUTORIAL_ID.FEED_CALENDAR) {
			this.props.doUnlockBody(LayoutTriggers.Tutorial);
		}

		this.handleSecondStep(TUTORIAL_ID.FEED_OVERVIEW, TUTORIAL_ID.FEED_CALENDAR, 1000);
	};

	public handleSecondStep = (currentStep: TutorialIdType, secondStep: TutorialIdType, delay: number) => {
		if (this.visibleTutorial === currentStep) {
			delayed(() => {
				this.props.doTriggerTutorial(secondStep, delay);
			}, 0);
		}
	};

	get isVisible(): boolean {
		const { visibleTutorial } = this.props;

		return visibleTutorial !== null;
	}

	get visibleTutorial(): TriggeredTutorialType {
		return this.isVisible ? this.props.visibleTutorial : null;
	}

	get className(): string {
		const className: string[] = [];

		if (this.visibleTutorial === TUTORIAL_ID.FEED_OVERVIEW) {
			className.push('m-tutorial--header');
		}

		if (this.visibleTutorial === TUTORIAL_ID.FEED_CALENDAR) {
			className.push('m-tutorial--calendar');
		}

		if (this.isVisible) {
			className.push('state-m-tutorial--animate-in');
		}

		return className.join(' ');
	}

	public renderInner() {
		switch (this.visibleTutorial) {
			case TUTORIAL_ID.FEED_OVERVIEW:
				return <TutorialFeedOverview />;
			case TUTORIAL_ID.FEED_CALENDAR:
				return <TutorialFeedCalendar />;
			case TUTORIAL_ID.CARD_DETAIL:
				return <TutorialCardDetail />;
			default:
				return <div />;
		}
	}

	public render() {
		return (
			<div className={`m-tutorial ${this.className}`} data-t-name="Tutorial" ref={this.componentRef}>
				<div className="m-tutorial__inner">
					{this.renderInner()}
					<div className="m-tutorial__button">
						<CtaWave
							onClick={this.handleClick}
							text={i18n('react.impulsCoach.components.tutorial.btnLabel')}
							icon="completed"
						/>
					</div>
				</div>
			</div>
		);
	}
}

export default withLayout(memo(Tutorial));
